import React from 'react';
import { toast } from 'react-toastify';
import { getCustomerById, updateCustomer } from '../../../../../helpers/customer-helper';
import {
    deleteFileAsset,
    detachFileReference,
    getFileAssetBlob,
    uploadFileAsset,
} from '../../../../../helpers/file-helper';

const normalizeDateForInput = (value) => {
    if (!value) return '';

    const raw = String(value);
    if (raw.length >= 10 && raw[4] === '-' && raw[7] === '-') {
        return raw.slice(0, 10);
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return date.toISOString().slice(0, 10);
};

export const useCustomerDetailScreen = ({ token, id, navigate, location, logoInputRef }) => {
    const [customer, setCustomer] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
    const [logoImageError, setLogoImageError] = React.useState(false);
    const [privateLogoUrl, setPrivateLogoUrl] = React.useState('');
    const [isEditingProfile, setIsEditingProfile] = React.useState(false);
    const [isSavingProfile, setIsSavingProfile] = React.useState(false);
    const [profileForm, setProfileForm] = React.useState({
        name: '',
        description: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        joinDate: '',
        status: 'ACTIVE',
    });
    const [activeTab, setActiveTab] = React.useState('general');

    const detailTabs = React.useMemo(() => ([
        { key: 'general', label: 'General' },
        { key: 'projects', label: 'Proyectos' },
    ]), []);

    const statusClassMap = {
        ACTIVE: 'bg-soft-success text-success',
        SUSPENDED: 'bg-soft-warning text-warning',
        INACTIVE: 'bg-soft-danger text-danger',
    };

    const statusActionsMap = {
        ACTIVE: [
            { value: 'INACTIVE', label: 'Desactivar', className: 'btn btn-light-brand' },
            { value: 'SUSPENDED', label: 'Suspender', className: 'btn btn-warning' },
        ],
        SUSPENDED: [
            { value: 'ACTIVE', label: 'Activar', className: 'btn btn-success' },
            { value: 'INACTIVE', label: 'Desactivar', className: 'btn btn-light-brand' },
        ],
        INACTIVE: [
            { value: 'ACTIVE', label: 'Activar', className: 'btn btn-success' },
            { value: 'SUSPENDED', label: 'Suspender', className: 'btn btn-warning' },
        ],
    };

    const statusOptions = React.useMemo(() => ([
        { value: 'ACTIVE', label: 'Activo' },
        { value: 'SUSPENDED', label: 'Suspendido' },
        { value: 'INACTIVE', label: 'Inactivo' },
    ]), []);

    React.useEffect(() => {
        const fetchCustomer = async () => {
            if (!token || !id) {
                setError('No se pudo obtener el cliente.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getCustomerById(token, id);

            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener el cliente.');
                setCustomer(null);
                setIsLoading(false);
                return;
            }

            setCustomer(resp.data || null);

            const fetchedCustomerName = resp?.data?.name;
            const currentCustomerName = location.state?.customerName;

            if (fetchedCustomerName && fetchedCustomerName !== currentCustomerName) {
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...(location.state || {}),
                        customerName: fetchedCustomerName,
                    },
                });
            }

            setIsLoading(false);
        };

        fetchCustomer();
    }, [token, id, navigate, location.pathname, location.state]);

    React.useEffect(() => {
        setLogoImageError(false);
    }, [customer?.logoFile?.url]);

    React.useEffect(() => {
        let isMounted = true;
        let objectUrl = '';

        const loadPrivateLogo = async () => {
            if (!token || !customer?.logoFile?._id || customer?.logoFile?.url) {
                setPrivateLogoUrl('');
                return;
            }

            const blobResp = await getFileAssetBlob(token, customer.logoFile._id);
            if (!blobResp?.success || !blobResp?.data || !isMounted) {
                return;
            }

            objectUrl = window.URL.createObjectURL(blobResp.data);
            setPrivateLogoUrl(objectUrl);
            setLogoImageError(false);
        };

        loadPrivateLogo();

        return () => {
            isMounted = false;
            if (objectUrl) {
                window.URL.revokeObjectURL(objectUrl);
            }
        };
    }, [token, customer?.logoFile?._id, customer?.logoFile?.url]);

    React.useEffect(() => {
        if (!customer) {
            return;
        }

        setProfileForm({
            name: customer.name || '',
            description: customer.description || '',
            contactName: customer.contactName || '',
            contactEmail: customer.contactEmail || '',
            contactPhone: customer.contactPhone || '',
            joinDate: normalizeDateForInput(customer.joinDate),
            status: customer.status || 'ACTIVE',
        });
    }, [customer]);

    const formatDate = React.useCallback((isoDate) => {
        if (!isoDate) return '-';

        const date = new Date(isoDate);
        if (Number.isNaN(date.getTime())) return '-';

        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }, []);

    const handleProfileInputChange = React.useCallback((event) => {
        const { name, value } = event.target;
        setProfileForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    }, []);

    const handleStartEditProfile = React.useCallback(() => {
        setError('');
        setIsEditingProfile(true);
    }, []);

    const handleCancelEditProfile = React.useCallback(() => {
        if (customer) {
            setProfileForm({
                name: customer.name || '',
                description: customer.description || '',
                contactName: customer.contactName || '',
                contactEmail: customer.contactEmail || '',
                contactPhone: customer.contactPhone || '',
                joinDate: normalizeDateForInput(customer.joinDate),
                status: customer.status || 'ACTIVE',
            });
        }

        setError('');
        setIsEditingProfile(false);
    }, [customer]);

    const handleSaveProfile = React.useCallback(async () => {
        if (!token || !id || isSavingProfile) {
            return;
        }

        setIsSavingProfile(true);
        setError('');
        const savingToastId = toast.loading('Guardando cambios del cliente...');

        const payload = {
            name: profileForm.name,
            description: profileForm.description,
            contactName: profileForm.contactName,
            contactEmail: profileForm.contactEmail,
            contactPhone: profileForm.contactPhone,
            joinDate: profileForm.joinDate,
            status: profileForm.status,
        };

        const resp = await updateCustomer(token, id, payload);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar el cliente.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSavingProfile(false);
            return;
        }

        setCustomer(resp.data || null);
        setIsEditingProfile(false);
        setIsSavingProfile(false);
        toast.update(savingToastId, {
            render: resp?.message || 'Cliente actualizado correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
    }, [token, id, isSavingProfile, profileForm]);

    const handleStatusChange = React.useCallback(async (nextStatus) => {
        if (!token || !id || !nextStatus || isUpdatingStatus) {
            return;
        }

        setIsUpdatingStatus(true);
        setError('');

        const resp = await updateCustomer(token, id, { status: nextStatus });

        if (!resp?.success) {
            setError(resp?.message || 'No se pudo actualizar el estatus del cliente.');
            setIsUpdatingStatus(false);
            return;
        }

        setCustomer(resp.data || null);
        setIsUpdatingStatus(false);
    }, [token, id, isUpdatingStatus]);

    const handleClickLogoUpload = React.useCallback(() => {
        if (!logoInputRef.current || isUploadingLogo) {
            return;
        }

        logoInputRef.current.click();
    }, [logoInputRef, isUploadingLogo]);

    const handleLogoFileChange = React.useCallback(async (event) => {
        const nextFile = event.target.files?.[0];

        if (!nextFile || !token || !id || isUploadingLogo) {
            if (event.target) {
                event.target.value = '';
            }
            return;
        }

        const previousLogoFileId = customer?.logoFile?._id || '';

        setIsUploadingLogo(true);
        setError('');

        const uploadResp = await uploadFileAsset(token, {
            file: nextFile,
            purpose: 'customer_logo',
            tags: ['customer', 'logo'],
            metadata: {
                source: 'customer_detail',
            },
            referenceType: 'CUSTOMER',
            referenceId: id,
            referenceField: 'logo',
            referenceLabel: customer?.name || 'Logo de cliente',
            isPublic: true,
        });

        const uploadedFileId = uploadResp?.data?._id;

        if (!uploadResp?.success || !uploadedFileId) {
            setError(uploadResp?.message || 'No se pudo subir el logo del cliente.');
            setIsUploadingLogo(false);
            if (event.target) {
                event.target.value = '';
            }
            return;
        }

        const updateResp = await updateCustomer(token, id, {
            logoFile: uploadedFileId,
        });

        if (!updateResp?.success) {
            await deleteFileAsset(token, uploadedFileId);
            setError(updateResp?.message || 'No se pudo guardar el logo en el cliente.');
            setIsUploadingLogo(false);
            if (event.target) {
                event.target.value = '';
            }
            return;
        }

        setCustomer(updateResp?.data || null);

        if (previousLogoFileId && previousLogoFileId !== uploadedFileId) {
            await detachFileReference(token, previousLogoFileId, {
                referenceType: 'CUSTOMER',
                referenceId: id,
                referenceField: 'logo',
                deleteIfOrphan: true,
            });
        }

        setIsUploadingLogo(false);
        if (event.target) {
            event.target.value = '';
        }
    }, [token, id, isUploadingLogo, customer]);

    const logoUrl = customer?.logoFile?.url || privateLogoUrl;
    const defaultLogoUrl = '/assets/images/avatar/default.png';
    const customerLogoSrc = !logoImageError && logoUrl ? logoUrl : defaultLogoUrl;
    const statusActions = statusActionsMap[customer?.status] || [];

    return {
        customer,
        isLoading,
        error,
        isUpdatingStatus,
        isUploadingLogo,
        customerLogoSrc,
        isEditingProfile,
        isSavingProfile,
        profileForm,
        activeTab,
        detailTabs,
        formatDate,
        statusClassMap,
        statusActions,
        statusOptions,
        setActiveTab,
        setLogoImageError,
        handleProfileInputChange,
        handleStartEditProfile,
        handleCancelEditProfile,
        handleSaveProfile,
        handleStatusChange,
        handleClickLogoUpload,
        handleLogoFileChange,
    };
};
